'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Users, Crown, Radio, Search, SortAsc, Star, BarChart2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { useLang } from '@/lib/i18n/LanguageContext';

interface Channel {
  id: string;
  creatorId: string;
  name: string;
  description: string | null;
  profileImage: string | null;
  price: number;
  isFree: boolean;
  category: string;
  status: string;
  subscriberCount: number;
  totalPosts: number;
  premiumPosts: number;
  creator: { id: string; username: string; displayName: string; image: string | null };
  createdAt: string;
}

// (2026-09-05) No demo/placeholder channels any more — an empty marketplace shows the empty state.
const CATEGORIES = ['All', 'Signals', 'Analysis', 'Education', 'Community'];
// Category accent — mapped onto the warm-light theme's semantic tokens (color + matching muted
// bg + a legible foreground for solid avatar fills) instead of hand-picked hex, so category
// chips/bars/avatars stay consistent with the rest of the site's color language.
const CATEGORY_STYLES: Record<string, { color: string; muted: string; fg: string }> = {
  Signals: { color: 'var(--accent)', muted: 'var(--accent-muted)', fg: 'var(--bg-base)' },
  Analysis: { color: 'var(--info)', muted: 'var(--info-muted)', fg: '#ffffff' },
  Education: { color: 'var(--bullish)', muted: 'var(--bullish-muted)', fg: '#ffffff' },
  Community: { color: 'var(--win)', muted: 'var(--win-muted)', fg: '#ffffff' },
};
const DEFAULT_CAT_STYLE = CATEGORY_STYLES.Signals;

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'most-subscribers', label: 'Most Subscribers' },
  { value: 'highest-earning', label: 'Highest Earning' },
  { value: 'lowest-price', label: 'Lowest Price' },
  { value: 'highest-price', label: 'Highest Price' },
];

export default function ChannelsPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [pricing, setPricing] = useState<'all' | 'paid' | 'free'>('all');

  useEffect(() => {
    fetchChannels();
  }, [activeCategory, sort]);

  const fetchChannels = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCategory !== 'All') params.set('category', activeCategory);
      params.set('sort', sort);
      const res = await fetch(`/api/channels?${params}`);
      if (res.ok) {
        const data = await res.json();
        setChannels(data.channels ?? []);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  const filtered = channels.filter(c => {
    const matchesSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.creator?.username?.toLowerCase().includes(search.toLowerCase()) ||
      c.creator?.displayName?.toLowerCase().includes(search.toLowerCase());
    const matchesPricing = pricing === 'all' || (pricing === 'paid' ? !c.isFree : c.isFree);
    return matchesSearch && matchesPricing;
  });

  // Featured = paid channels with most subscribers (top 2)
  const featured = filtered.filter(c => !c.isFree).sort((a, b) => b.subscriberCount - a.subscriberCount).slice(0, 2);
  const showFeatured = !search && sort === 'newest' && featured.length > 0;
  // Don't render the featured channels a second time in the grid below.
  const featuredIds = new Set(showFeatured ? featured.map(c => c.id) : []);
  const regular = filtered.filter(c => !featuredIds.has(c.id));

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Radio size={22} style={{ color: 'var(--accent)' }} /> {t('Trader Channels')}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{t('Subscribe to premium trading content from top traders')}</p>
          </div>
          {user && (
            <Link href="/channels/create" className="btn-primary flex items-center gap-2 px-4 py-2.5 text-sm">
              <Plus size={15} /> {t('Create Channel')}
            </Link>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('Search channels or traders…')}
            className="input-base w-full pl-9 pr-4 py-2.5 text-sm"
          />
        </div>

        {/* Pricing filter */}
        <div className="flex gap-2 mb-3">
          {([['all', 'All groups'], ['paid', 'Paid'], ['free', 'Free']] as const).map(([val, label]) => {
            const on = pricing === val;
            const tone = val === 'paid' ? 'var(--win)' : 'var(--accent)';
            const toneMuted = val === 'paid' ? 'var(--win-muted)' : 'var(--accent-muted)';
            return (
              <button key={val} onClick={() => setPricing(val)}
                className="px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all"
                style={{
                  background: on ? toneMuted : 'var(--bg-card)',
                  border: `1px solid ${on ? tone : 'var(--border)'}`,
                  color: on ? tone : 'var(--text-secondary)',
                }}>
                {t(label)}
              </button>
            );
          })}
        </div>

        {/* Category filter + Sort — dark-ink-filled active pill per FeedFilterBar's market-tab convention */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex gap-2 overflow-x-auto pb-1 flex-1">
            {CATEGORIES.map(cat => {
              const active = activeCategory === cat;
              return (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  className="px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all"
                  style={{
                    background: active ? 'var(--text-primary)' : 'var(--bg-card)',
                    border: `1px solid ${active ? 'var(--text-primary)' : 'var(--border)'}`,
                    color: active ? 'var(--bg-base)' : 'var(--text-secondary)',
                  }}>
                  {t(cat)}
                </button>
              );
            })}
          </div>
          <div className="relative shrink-0">
            <select value={sort} onChange={e => setSort(e.target.value)}
              className="input-base appearance-none pl-3 pr-8 py-1.5 text-sm cursor-pointer">
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
              ))}
            </select>
            <SortAsc size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="skeleton h-52 rounded-[20px]" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div>
            {/* Coming Soon hero */}
            <div className="card-base text-center py-12 mb-6">
              <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--accent-muted)', border: '1px solid var(--accent)' }}>
                <Radio size={28} style={{ color: 'var(--accent)' }} />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {search ? t('No channels match your search') : pricing === 'paid' ? t('No paid groups yet') : pricing === 'free' ? t('No free channels yet') : activeCategory !== 'All' ? t('No channels in this category yet') : t('Channels launching soon')}
              </h2>
              <p className="text-sm mb-5 max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
                {search ? t('Try a different search term or browse all categories') : t('Top-performing traders will be able to create premium channels. Subscribe to get real-time signals, analysis, and exclusive trade ideas.')}
              </p>
              {user && !search && (
                <Link href="/channels/create" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm">
                  <Plus size={14} /> {t('Create a Channel')}
                </Link>
              )}
            </div>

            {/* Preview cards showing what channels will look like */}
            {!search && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <Star size={13} style={{ color: 'var(--text-muted)' }} />
                  <h2 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{t('What to expect')}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 opacity-60">
                  {[
                    { name: 'Forex Signals Pro', category: 'Signals', desc: 'Daily EUR/USD, GBP/USD setups with exact entry, SL & TP levels.', price: '$29', subs: '—', posts: '—' },
                    { name: 'Crypto Analysis Lab', category: 'Analysis', desc: 'Deep-dive BTC & ETH technical analysis with weekly outlook reports.', price: '$19', subs: '—', posts: '—' },
                    { name: 'Trading Academy', category: 'Education', desc: 'Learn risk management, position sizing, and chart reading from scratch.', price: 'Free', subs: '—', posts: '—' },
                    { name: 'Indices Community', category: 'Community', desc: 'Discuss S&P 500, NASDAQ, and DAX setups with fellow traders.', price: 'Free', subs: '—', posts: '—' },
                  ].map((ch, i) => {
                    const catStyle = CATEGORY_STYLES[ch.category] || DEFAULT_CAT_STYLE;
                    return (
                      <div key={i} className="card-base overflow-hidden">
                        <div className="h-1" style={{ background: `linear-gradient(90deg, ${catStyle.color}, transparent)` }} />
                        <div className="p-5">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold shrink-0"
                              style={{ background: catStyle.muted, color: catStyle.color }}>
                              {ch.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{ch.name}</h3>
                              <span className="text-xs px-1.5 py-0.5 rounded-full mt-1 inline-block" style={{ background: catStyle.muted, color: catStyle.color }}>{t(ch.category)}</span>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="font-mono-nums text-sm font-bold" style={{ color: ch.price === 'Free' ? 'var(--bullish)' : 'var(--win)' }}>{ch.price === 'Free' ? t('Free') : ch.price}</p>
                              {ch.price !== 'Free' && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('/month')}</p>}
                            </div>
                          </div>
                          <p className="text-xs leading-relaxed mb-3 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{t(ch.desc)}</p>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                              <Users size={11} /> {ch.subs} {t('subscribers')}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ) : (
          <div>
            {/* Featured premium channels */}
            {showFeatured && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Star size={13} style={{ color: 'var(--win)' }} />
                  <h2 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{t('Featured Premium')}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {featured.map(channel => (
                    <FeaturedChannelCard key={channel.id} channel={channel} currentUserId={user?.id} />
                  ))}
                </div>
              </div>
            )}

            {/* All channels */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {regular.map(channel => (
                <ChannelCard key={channel.id} channel={channel} currentUserId={user?.id} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturedChannelCard({ channel, currentUserId }: { channel: Channel; currentUserId?: string }) {
  const { t } = useLang();
  const catStyle = CATEGORY_STYLES[channel.category] || DEFAULT_CAT_STYLE;
  const isOwner = currentUserId === channel.creatorId;

  return (
    <Link href={`/channels/${channel.id}`}
      className="block rounded-[20px] overflow-hidden transition-all group relative"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--win)', boxShadow: '0 2px 24px rgba(28,43,38,0.10)' }}>
      {/* Gold accent bar — premium/featured context */}
      <div className="h-1" style={{ background: 'linear-gradient(90deg, var(--win), transparent)' }} />

      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold shrink-0"
            style={{
              background: channel.profileImage ? `url(${channel.profileImage}) center/cover` : catStyle.muted,
              color: catStyle.color,
              border: `1px solid ${catStyle.muted}`,
            }}>
            {!channel.profileImage && channel.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{channel.name}</h3>
              <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full font-semibold"
                style={{ background: 'var(--win-muted)', color: 'var(--win)' }}>
                <Crown size={9} /> {t('Premium')}
              </span>
              {isOwner && (
                <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                  {t('Yours')}
                </span>
              )}
            </div>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>@{channel.creator?.username}</p>
          </div>
          <div className="shrink-0 text-right">
            <p className="font-mono-nums text-base font-bold" style={{ color: 'var(--win)' }}>${channel.price}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('/month')}</p>
          </div>
        </div>

        {channel.description && (
          <p className="text-xs leading-relaxed mb-3 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{channel.description}</p>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-xs font-mono-nums" style={{ color: 'var(--text-muted)' }}>
              <Users size={11} /> {channel.subscriberCount} {t('subscribers')}
            </span>
            {channel.totalPosts > 0 && (
              <span className="flex items-center gap-1 text-xs font-mono-nums" style={{ color: 'var(--text-muted)' }}>
                <BarChart2 size={11} /> {channel.totalPosts} {t('posts')}
              </span>
            )}
          </div>
          <span className="text-xs font-semibold px-3 py-1.5 rounded-lg"
            style={{ background: 'var(--win-muted)', color: 'var(--win)' }}>
            {t('Subscribe →')}
          </span>
        </div>
      </div>
    </Link>
  );
}

function ChannelCard({ channel, currentUserId }: { channel: Channel; currentUserId?: string }) {
  const { t } = useLang();
  const catStyle = CATEGORY_STYLES[channel.category] || DEFAULT_CAT_STYLE;
  const isOwner = currentUserId === channel.creatorId;

  return (
    <Link href={`/channels/${channel.id}`}
      className="card-base block overflow-hidden transition-all group"
      style={{ boxShadow: '0 2px 24px rgba(28,43,38,0.10)' }}>
      {/* Category color bar */}
      <div className="h-1" style={{ background: `linear-gradient(90deg, ${catStyle.color}, transparent)` }} />

      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          {/* 40px rounded-square avatar per the FeedLeftSidebar channel-badge convention */}
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-base font-bold shrink-0"
            style={{
              background: channel.profileImage ? `url(${channel.profileImage}) center/cover` : catStyle.muted,
              color: catStyle.color,
            }}>
            {!channel.profileImage && channel.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>{channel.name}</h3>
              {isOwner && (
                <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                  {t('Your channel')}
                </span>
              )}
            </div>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>@{channel.creator?.username}</p>
          </div>
          <div className="shrink-0 text-right">
            {channel.isFree ? (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--bullish-muted)', color: 'var(--bullish)' }}>
                {t('Free')}
              </span>
            ) : (
              <span className="font-mono-nums text-xs font-semibold flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: 'var(--win-muted)', color: 'var(--win)' }}>
                <Crown size={9} /> ${channel.price}{t('/mo')}
              </span>
            )}
          </div>
        </div>

        {channel.description && (
          <p className="text-xs leading-relaxed mb-3 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{channel.description}</p>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: catStyle.muted, color: catStyle.color }}>
              {t(channel.category)}
            </span>
            <span className="flex items-center gap-1 text-xs font-mono-nums" style={{ color: 'var(--text-muted)' }}>
              <Users size={11} /> {channel.subscriberCount}
            </span>
            {channel.totalPosts > 0 && (
              <span className="flex items-center gap-1 text-xs font-mono-nums" style={{ color: 'var(--text-muted)' }}>
                <BarChart2 size={11} /> {channel.totalPosts}
              </span>
            )}
          </div>
          <span className="text-xs font-medium px-3 py-1 rounded-lg" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
            {t('View →')}
          </span>
        </div>
      </div>
    </Link>
  );
}
